import Utils from "../basic/Utils";
import DitapPopup from "./DitapPopup";

/**
 * 객체 속성 표시 팝업 클래스.</br>
 * A Pop-up that shows the properties of an object.
 * @extends DitapPopup
 *
 * @alias PopupProperty
 * @constructor
 *
 * @param {object} options 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {DitapViewer} [options.viewer] 사용할 Ditap Viewer 인스턴스.</br>he DitapViewer instance to use.
 * @param {object} [options.selectedObject] 선택된 객체.</br>The selected object.
 * @param {string[]} [options.properties] 선택 객체의 속성.<br>The properties of selected object.
 * @param {Function} [options.propertyCallback] 속성 콜백 함수.<br>property callback Function.
 *
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *   126.97759025002786,
 *   37.56679755138474
 * );
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *   126.97764810657144,
 *   37.566518888624664
 * );
 *
 * // LinestringEntity instance 생성
 * const linestringEntity = new Ditap.LinestringEntity(viewer, [
 *   position1,
 *   position2,
 * ]);
 *
 * const linestringEntityProperty = 
 *     {
 *        properties: {
 *          id: "line2",
 *          position1 : position1,
 *          position2 : position2
 *        }
 *     };
 * 
 * // 비동기로 속성값을 요청하는 promise를 반환하는 함수
 * function getPropertiesPromise(selectedObject) {
 *   // 첫번째 파라미터로 받은 선택된 객체 인스턴스를 활용하여 콜백함수 작성
 *   if (!selectedObject) {
 *     return;
 *   }
 *   return new Promise((resolve, reject) => {
 *     setTimeout(() => {
 *       resolve([
 *         {
 *           title: "newObject",
 *           properties: {
 *             name: "objectName",
 *             content: "objectContent",
 *           },
 *         },
 *         {
 *           title: "newObject2",
 *           properties: {
 *             name: "objectName2",
 *             content: "objectContent2",
 *           },
 *         },
 *       ]);
 *     }, 2000);
 *   });
 * }
 *
 * const popup = new Ditap.PopupProperty(
 *   {
 *     viewer: viewer,
 *     selectedObject: linestringEntity,
 *     properties: [linestringEntityProperty],
 *     propertyCallback: getPropertiesPromise,
  });
 *
 */
class PopupProperty extends DitapPopup {
  constructor(options) {
    const viewer = options.viewer;
    const selectedObject = options.selectedObject;
    const properties = options.properties;
    const propertyCallback = options.propertyCallback;
    const closeCallback = options.closeCallback;

    // 부모
    const parent = viewer.container;
    super(parent, options.viewer.ditapPaneManager);

    // 속성박스 생성
    const propertyBox = this.createPropertyBox();

    // 헤더 추가
    this.prependHeader(propertyBox["header"]["title"]);
    // this.appendHeader(propertyBox["header"]["closeBtn"]);

    // 컨텐츠 추가
    this.appendSubTitle(propertyBox["content"]["subTitle"]);
    this.appendContent(propertyBox["content"]["propertyList"]);
    // this.add(this.mainPopup);

    // 노드 세팅
    this.propertyUl = propertyBox["content"]["propertyList"];

    // 뷰어 세팅
    this.viewer = viewer;

    this.selectedObject = selectedObject;

    // 속성 리스트 삭제
    this.removePropertyList();

    // 속성 리스트 추가
    this.properties = properties;
    this.addProperties(properties);

    // 팝업 오픈
    this.open();

    this.changeCloseBtn();
    const isMobile = Utils.isBodyWidthLessThan600();
    if (!isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    // 푸터 삭제
    this.footer.remove();

    // 스크롤 추가
    this.wrapper.classList.add("ditap-scroll-popup");
    // this.wrapper.classList.add("ditap-scroll-popup");
    this.wrapper.classList.add("ditap-popup-mobile");

    // 콜백함수 저장
    this.propertyCallback = propertyCallback;

    // 콜백함수 실행
    this.runPropertyCallback();
    this.closeCallback = closeCallback;
  }

  /**
   * Run property Callback function.
   * @param {object} properties The properties of Entity
   */
  runPropertyCallback() {
    if (this.propertyCallback instanceof Function) {
      this.propertyCallback(this.selectedObject)
        .then((response) => {
          if (response instanceof Array) {
            this.properties = [...this.properties, ...response];

            // 모든 속성 삭제
            this.removePropertyList();

            // 속성 list 생성
            this.addProperties(this.properties);
          } else {
            throw Error("propertyCallback response type error");
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } else if (!(this.propertyCallback === undefined)) {
      console.error("PopupProperty propertyCallback type error");
    }
  }

  /**
   * 엔티티의 속성.</br>
   * Properties of the entity.
   * @type {object}
   * @readonly
   */
  set properties(properties) {
    this._properties = properties;
  }
  get properties() {
    return this._properties;
  }

  /**
   * 속성 조회 할 엔티티.</br>
   * Entitiy to query for properties.
   * @type {Entity}
   * @readonly
   */
  set selectedObject(selectedObject) {
    this._selectedObject = selectedObject;
  }
  get selectedObject() {
    return this._selectedObject;
  }

  /*
   * 팝업 객체 속성 목록 삭제.</br>
   * remove a list of object properties.
   */
  removePropertyList() {
    while (this.propertyUl.firstChild) {
      this.propertyUl.removeChild(this.propertyUl.lastChild);
    }
  }

  /*
   * 팝업 객체 속성 목록 추가.</br>
   * Adds a list of object properties to the PopupProperty.
   * @param {object} properties 추가할 객체 속성.</br> The properties of Entity.
   */
  addProperties(properties) {
    for (let i = 0; i < properties.length; i++) {
      const property = properties[i];
      this.createPropertyDiv(property.properties);
    }
  }

  /*
   * PopupProperty에 div를 생성합니다.
   * @param {object} properties The properties of Entity
   */
  createPropertyDiv(properties) {
    // 속성 리스트 추가
    const liColor = ["red", "yellow", "green"];
    let liNum = 0;
    if (properties) {
      const keys = Object.keys(properties);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (properties.hasOwnProperty(key)) {
          const value = properties[key];
          const li = document.createElement("li");
          const p1 = document.createElement("p");
          const p2 = document.createElement("p");

          li.className = `ditap-property-item property-${
            liColor[(liNum + 1) % 3]
          }-dot`;
          p1.className = "ditap-property-key";
          p2.className = "ditap-property-value ditap-property-maxwidth";

          p1.textContent = key;
          p2.textContent = value;

          li.appendChild(p1);
          li.appendChild(p2);

          this.propertyUl.appendChild(li);

          liNum++;
        }
      }
    }
  }

  /*
   * 속성 팝업 요소 생성.</br>
   * Create PopupProperty Element.
   */
  createPropertyBox() {
    this.wrapper.classList.add(
      "popup-property",
      "header-top",
      "ditap-popup-mobile",
      "ditap-mobile-bottom",
      "draggable"
    );
    // * 속성박스 생성
    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "속성";
    const closeBtn = document.createElement("button");
    closeBtn.classList.add("ditap-popup-close-btn", "js-ditap-style-close-btn");

    // # 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "속성의 정보를 확인하세요.";
    const propertyList = document.createElement("ul");
    propertyList.classList.add("ditap-popup-property-list");

    return {
      header: {
        title: title,
        closeBtn: closeBtn,
      },
      content: {
        subTitle: subTitle,
        propertyList: propertyList,
      },
    };
  }

  /**
   * 팝업 닫기.</br>
   * Close popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>  If no parameter is entered, close wrapper.
   */
  close(element = undefined) {
    if (element) {
      element.classList.add("hide");
    } else {
      this.wrapper.classList.add("hide");
    }

    this.ditapPaneManager?.removePane(element ? element : this.wrapper);

    if (this.closeCallback instanceof Function) {
      this.closeCallback(this.selectedObject);
    }
  }
}
export default PopupProperty;
